import connection from "../connection";
import API from "@shared/config/services/url.config";

export const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onloadend = () => {
            resolve(reader.result);
        };

        reader.onerror = () => {
            reject(new Error("Error al convertir el archivo a base64"));
        };

        reader.readAsDataURL(file);
    });
}

/**
 * Sube un archivo (como base64) a un servidor transactional en un scope privado.
 *
 * Esta función convierte un archivo o una cadena base64 en contenido subible y lo envía
 * a una ruta protegida (`/transactional/private`) usando encabezados de autorización específicos.
 *
 * @param {Object} options - Opciones para la subida del archivo.
 * @param {string} options.name - Nombre base del archivo (sin extensión). Se usará para generar un nombre único.
 * @param {File} [options.file] - Objeto `File` (por ejemplo, desde un `<input type="file">`).
 * @param {string} [options.fileBase64] - Cadena base64 del archivo. Si no se pasa, se genera desde `file`.
 * @param {string} [options.location='files'] - Carpeta (scope) donde se almacenará el archivo en el servidor.
 *
 * @returns {Promise<{ estado: number, url?: string, message?: string }>} 
 * - `estado: 1` si fue exitoso, e incluye la `url` para acceder al archivo.
 * - `estado: 0` si ocurrió un error, e incluye un `message` descriptivo.
 *
 * @example
 * const res = await uploadFile({
 *   name: 'avatar',
 *   file: selectedFile,
 *   location: 'usuarios'
 * });
 *
 * if (res.estado === 1) {
 *   console.log('Archivo subido con éxito:', res.url);
 * }
 */
export default async ({ name, file, fileBase64, location = 'files' }) => {
    const base64 = fileBase64 || await fileToBase64(file);
    const base = API.E2E || '/api/backend';

    // La subida la hace el BACKEND (proxy seguro): el front solo manda el base64 con su sesión.
    // La api-key del media-manager nunca está en el bundle del front.
    const res = await connection({
        method: 'POST',
        url: `${base}/archivos/subir`,
        body: { base64, name: name || 'archivo.png', location },
        credentials: 'include',
    });

    if (!res?.estado || !res?.response?.url) {
        return {
            estado: 0,
            status: res?.networkInfo?.status,
            message: res?.message || 'No se pudo subir el archivo. Reintentá en unos segundos.',
        };
    }

    return { estado: 1, url: res.response.url };
}
